import { reactive, ref } from "vue";

import { Flags } from "./featureDefinitions.js";
export { Flags } from "./featureDefinitions.js";

let API = null;

let clientsFeatureFlags = null;
export const featureFlagsLoaded = ref(false);
let featureFlagsRevision = 0;

// Reactive store of feature flag values keyed by flag id (e.g. "MEAT_MODULE").
// Property reads inside templates / computed / watchers are tracked by Vue,
// so callers of hasFeatureFlag() automatically re-render once flags load.
const FeatureFlagStore = reactive({});

let _devOverrideStore = null;

// Resolves once loadFeatureFlags() finishes (success or failure).
// Reset to a fresh pending promise on clearFeatureFlags() so a re-login
// can await a new load instead of seeing a stale resolved promise.
let resolveFlagsReady;
export let flagsReady = new Promise((resolve) => {
    resolveFlagsReady = resolve;
});

function resetFlagsReady() {
    featureFlagsLoaded.value = false;
    flagsReady = new Promise((resolve) => {
        resolveFlagsReady = resolve;
    });
}

export function setDevOverrideStore(store) {
    _devOverrideStore = store;
}

export function hasFeatureFlag(flag) {
    // Chat entitlement also gates jobs and push routing; a UI preview cannot grant it.
    if (_devOverrideStore && flag !== Flags.TeamChatModule) {
        const overrides = _devOverrideStore.state.devOverrides;
        if (overrides.active && flag in overrides.flagOverrides) {
            return overrides.flagOverrides[flag];
        }
    }
    return !!FeatureFlagStore[flag];
}

// A canonical feature denial revokes stale session visibility without changing
// other modules. A new login reloads the stored entitlements.
export function reconcileFeatureDenied(flag, error) {
    if (error?.status !== 403 || error?.data?.feature !== flag) return;
    FeatureFlagStore[flag] = false;
    if (Array.isArray(clientsFeatureFlags))
        clientsFeatureFlags = clientsFeatureFlags.filter((value) => value !== flag);
}

export function getRealFlagValue(flag) {
    return !!FeatureFlagStore[flag];
}

// Back-compat wrapper for existing callers — awaits the current flagsReady.
export function ensureFlagsAreSet() {
    return flagsReady;
}

export default class FeatureFlags {
    constructor(DataAPI) {
        API = DataAPI;
    }

    async hasFeatureFlagSync(flag) {
        if (!flag) return false;

        // Load the flags on first call
        if (!clientsFeatureFlags) {
            const revision = featureFlagsRevision;
            const client = await API.Client.GetSync();
            if (revision !== featureFlagsRevision) return false;
            clientsFeatureFlags = client.feature_flags;
        }

        // Check if the flag is present
        for (let i = 0; i < clientsFeatureFlags?.length; i++) {
            if (clientsFeatureFlags[i] == flag) return true;
        }

        return false;
    }

    // Load happens once per session. flagsReady resolves in finally so the
    // router guard never hangs even if the API call fails (the guard will see
    // an empty store and redirect to "/" rather than block indefinitely).
    async loadFeatureFlags() {
        const revision = featureFlagsRevision;
        const resolveThisLoad = resolveFlagsReady;
        try {
            if (!clientsFeatureFlags) {
                const featureFlagKeys = Object.keys(Flags);
                for (let i = 0; i < featureFlagKeys.length; i++) {
                    const flag = Flags[featureFlagKeys[i]];
                    const enabled = await this.hasFeatureFlagSync(flag);
                    if (revision !== featureFlagsRevision) return;
                    FeatureFlagStore[flag] = enabled;
                }
            }
        } finally {
            // clearFeatureFlags starts a new readiness cycle. An old request
            // finishing after logout must not resolve or repopulate that cycle.
            if (revision === featureFlagsRevision) {
                featureFlagsLoaded.value = true;
                resolveThisLoad();
            }
        }
    }

    clearFeatureFlags() {
        featureFlagsRevision += 1;
        clientsFeatureFlags = null;
        for (const key of Object.keys(FeatureFlagStore)) {
            delete FeatureFlagStore[key];
        }
        resetFlagsReady();
    }
}
